import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, AlertCircle } from "lucide-react";

const LIMIT_LABELS = {
  tournaments: { name: "Tornei", verb: "creare nuovi tornei", icon: "🏆" },
  categories: { name: "Categorie", verb: "aggiungere altre categorie", icon: "📋" },
  players: { name: "Giocatori per categoria", verb: "aggiungere altri giocatori", icon: "👤" },
};

const TrialLimitModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setInfo(e.detail);
      setOpen(true);
    };
    window.addEventListener("trial-limit-reached", handler);
    return () => window.removeEventListener("trial-limit-reached", handler);
  }, []);

  const lim = info?.limit_type ? LIMIT_LABELS[info.limit_type] : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-md max-w-md p-0 overflow-hidden" data-testid="trial-limit-modal">
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Crown className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide font-bold opacity-90">Periodo di prova</p>
              <DialogTitle className="text-lg font-heading font-bold leading-tight">
                Hai raggiunto un limite del trial
              </DialogTitle>
            </div>
          </div>
        </div>

        <DialogHeader className="sr-only">
          <DialogTitle>Limite del periodo di prova raggiunto</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                {lim ? (
                  <>
                    <p className="text-sm font-bold text-amber-900">
                      {lim.icon} Limite {lim.name.toLowerCase()} superato
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Sei nel periodo di prova gratuita (14 giorni). Hai raggiunto il limite previsto:
                      {" "}
                      <span className="font-bold">
                        {info?.limit_value} {lim.name.toLowerCase()}
                      </span>
                      {typeof info?.current_value === "number" && info.current_value > 0 ? (
                        <span className="opacity-80"> (attualmente {info.current_value})</span>
                      ) : null}
                      .
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-amber-900">{info?.message || "Limite del periodo di prova raggiunto."}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-green-700" />
              Con un abbonamento sblocchi:
            </p>
            <ul className="text-xs text-slate-600 space-y-1 ml-5 list-disc">
              <li>Tornei illimitati</li>
              <li>Categorie illimitate per torneo</li>
              <li>Giocatori illimitati per categoria</li>
              <li>Notifiche email e promemoria a tutti i partecipanti</li>
              <li>Esportazione PDF con loghi sponsor</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-sm flex-1"
            data-testid="trial-limit-close"
          >
            Più tardi
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              navigate("/subscription");
            }}
            className="rounded-sm flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase"
            data-testid="trial-limit-upgrade"
          >
            Attiva abbonamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrialLimitModal;
